import type {
    FileUpload,
    FileUploadError,
    FileUploadPending,
    FileUploadRemoved,
    FileUploadRemoving,
    FileUploadStaged,
    FileUploadSuccess,
    FileUploadUploading,
} from './fileUpload';

export const isFileUploadStaged = (fileUpload: FileUpload): fileUpload is FileUploadStaged =>
    fileUpload.state === 'staged';

export const isFileUploadSuccess = (fileUpload: FileUpload): fileUpload is FileUploadSuccess =>
    fileUpload.state === 'success';

export const isFileUploadError = (fileUpload: FileUpload): fileUpload is FileUploadError =>
    fileUpload.state === 'error';

export const isFileUploadUploading = (fileUpload: FileUpload): fileUpload is FileUploadUploading =>
    fileUpload.state === 'uploading';

export const isFileUploadPending = (fileUpload: FileUpload): fileUpload is FileUploadPending =>
    fileUpload.state === 'pending';

export const isFileUploadRemoving = (fileUpload: FileUpload): fileUpload is FileUploadRemoving =>
    fileUpload.state === 'removing';

export const isFileUploadRemoved = (fileUpload: FileUpload): fileUpload is FileUploadRemoved =>
    fileUpload.state === 'removed';

type RequiredMatchFileUpload<T> = {
    pendingCase: (fileUpload: FileUploadPending) => T;
    uploadingCase: (fileUpload: FileUploadUploading) => T;
    errorCase: (fileUpload: FileUploadError) => T;
    successCase: (fileUpload: FileUploadSuccess) => T;
    removingCase: (fileUpload: FileUploadRemoving) => T;
    removedCase: (fileUpload: FileUploadRemoved) => T;
    stagedCase: (fileUpload: FileUploadStaged) => T;
};

type DefaultMatchFileUpload<T> = { defaultCase: (fileUpload: FileUpload) => T } & Partial<RequiredMatchFileUpload<T>>;

type MatchFileUpload<T> = RequiredMatchFileUpload<T> | DefaultMatchFileUpload<T>;

export const matchFileUpload = <T>(fileUpload: FileUpload, match: MatchFileUpload<T>) => {
    if (isFileUploadPending(fileUpload) && match.pendingCase) {
        return match.pendingCase(fileUpload);
    }

    if (isFileUploadUploading(fileUpload) && match.uploadingCase) {
        return match.uploadingCase(fileUpload);
    }

    if (isFileUploadError(fileUpload) && match.errorCase) {
        return match.errorCase(fileUpload);
    }

    if (isFileUploadSuccess(fileUpload) && match.successCase) {
        return match.successCase(fileUpload);
    }

    if (isFileUploadRemoving(fileUpload) && match.removingCase) {
        return match.removingCase(fileUpload);
    }

    if (isFileUploadRemoved(fileUpload) && match.removedCase) {
        return match.removedCase(fileUpload);
    }

    if (isFileUploadStaged(fileUpload) && match.stagedCase) {
        return match.stagedCase(fileUpload);
    }

    if ('defaultCase' in match) {
        return match.defaultCase(fileUpload);
    }

    throw new Error('No match found for file upload');
};
